import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Shield, Clock, FileText, User, MapPin, Phone,
  Mail, Heart, Briefcase, Lock, Check, Loader2, MessageSquare, Send, Download
} from "lucide-react";
import { safeJsonRequest } from "../utils/safeFetch";
import { generateReferralDetailPDF } from "../utils/adminPdfExport";
import SSNCell from "../components/admin/SSNCell";
import { STATUS_OPTIONS, getStatusStyle, getStatusLabel } from "../components/admin/ReferralCard";

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
    <div className="px-5 py-3 border-b border-slate-700 flex items-center gap-2">
      {Icon && <Icon className="w-4 h-4 text-blue-400" />}
      <h3 className="text-sm font-bold text-white">{title}</h3>
    </div>
    <div className="px-5 py-4">{children}</div>
  </div>
);

const Field = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <div>
      <dt className="text-[11px] text-slate-500 uppercase tracking-wider mb-0.5">{label}</dt>
      <dd className="text-sm text-slate-200">{value}</dd>
    </div>
  );
};

const ReferralDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("");
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [noteSending, setNoteSending] = useState(false);

  const token = sessionStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) { navigate("/admin"); return; }
    loadReferral();
  }, [id]);

  const loadReferral = async () => {
    setLoading(true);
    try {
      const data = await safeJsonRequest(`/api/admin/referrals/${id}`, { token });
      setReferral(data);
      setCurrentStatus(data.status || "submitted");
      setNotes(data.notes || []);
    } catch (err) {
      if (err.status === 401) navigate("/admin");
      else if (err.status === 404) navigate("/admin/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === currentStatus) return;
    setStatusLoading(true);
    try {
      await safeJsonRequest(`/api/admin/referrals/${id}/status`, { method: "PATCH", body: { status: newStatus }, token });
      setCurrentStatus(newStatus);
    } catch { /* silent */ } finally { setStatusLoading(false); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setNoteSending(true);
    try {
      const data = await safeJsonRequest(`/api/admin/referrals/${id}/notes`, { method: "POST", body: { text: newNote.trim() }, token });
      setNotes((prev) => [...prev, data.note]);
      setNewNote("");
    } catch { /* silent */ } finally { setNoteSending(false); }
  };

  const handleDownloadPDF = async () => {
    if (!referral) return;
    try {
      await generateReferralDetailPDF(referral);
      safeJsonRequest(`/api/admin/referrals/${id}/notes`, {
        method: "POST",
        body: { text: `[SYSTEM] PDF exported` },
        token,
      }).then((data) => {
        setNotes((prev) => [...prev, data.note]);
      }).catch(() => {});
    } catch (err) {
      console.error("PDF generation failed:", err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <span className="w-8 h-8 border-3 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!referral) return null;

  const rd = referral.referral_data || {};
  const omhc = rd.omhc || {};
  const prp = rd.prp || {};
  const sigs = rd.signatures || {};
  const category = rd.referralCategory || "N/A";
  const clientName =
    [omhc.firstName, omhc.middleName, omhc.lastName].filter(Boolean).join(" ") ||
    [prp.firstName, prp.middleName, prp.lastName].filter(Boolean).join(" ") ||
    omhc.legalName || prp.legalName || "Unknown";
  const submittedDate = referral.submitted_at
    ? new Date(referral.submitted_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })
    : "N/A";

  const urgencyColor = { Urgent: "text-red-400 bg-red-900/40", "Semi-Urgent": "text-orange-400 bg-orange-900/40", Routine: "text-green-400 bg-green-900/40", Priority: "text-orange-400 bg-orange-900/40" }[rd.urgencyLevel] || "text-slate-400 bg-slate-700";

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 px-6 py-3 flex items-center gap-4">
        <button onClick={() => navigate("/admin/dashboard")} className="p-2 rounded-lg hover:bg-slate-700 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold">{clientName}</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${urgencyColor}`}>{rd.urgencyLevel || "N/A"}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusStyle(currentStatus)}`}>{getStatusLabel(currentStatus)}</span>
            <span className="text-xs text-slate-500">{category}</span>
            <span className="text-xs text-slate-600">|</span>
            <span className="text-xs text-slate-500">{submittedDate}</span>
          </div>
        </div>
        <button
          onClick={handleDownloadPDF}
          className="px-3 py-2 bg-emerald-700 hover:bg-emerald-600 rounded-lg text-xs text-white font-medium transition-colors flex items-center gap-1.5"
          data-testid="download-pdf-btn"
        >
          <Download className="w-4 h-4" /> Download PDF
        </button>
        <Shield className="w-5 h-5 text-blue-400" />
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {/* Status Workflow */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-400 mr-1">Status:</span>
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleStatusChange(opt.value)}
              disabled={statusLoading}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                currentStatus === opt.value
                  ? `${opt.color} border-current ring-1 ring-current/30`
                  : "bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-500"
              }`}
              data-testid={`detail-status-${opt.value}`}
            >
              {currentStatus === opt.value && <Check className="w-3 h-3 inline mr-1" />}
              {opt.label}
              {statusLoading && <Loader2 className="w-3 h-3 inline ml-1 animate-spin" />}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Referral Source Info */}
          <Section title="Referral Source" icon={FileText}>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Field label="Date" value={rd.referralDate} />
              <Field label="Source" value={rd.referralSource} />
              <Field label="Provider" value={rd.referringProvider} />
              <Field label="Credentials" value={rd.credentials} />
              <Field label="NPI" value={rd.npi} />
              <Field label="Phone" value={rd.phone} />
              <Field label="Fax" value={rd.fax} />
              <Field label="Email" value={rd.email} />
              <Field label="Contact Method" value={rd.contactMethod} />
              <Field label="Category" value={category} />
              <Field label="Client Type" value={rd.clientType} />
              <Field label="Urgency" value={rd.urgencyLevel} />
              <Field label="Timeframe" value={rd.preferredTimeframe} />
              <Field label="Referral Status" value={rd.referralStatus} />
            </dl>
          </Section>

          {/* SSN — Protected */}
          <Section title="Protected Health Information" icon={Lock}>
            <div className="space-y-3">
              {referral.has_ssn_omhc && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">OMHC SSN:</span>
                  <SSNCell referralId={id} ssnField="omhc" maskedSsn={referral.ssn_omhc_masked} hasSsn={true} token={token} onAuditEvent={() => {}} />
                </div>
              )}
              {referral.has_ssn_prp && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">PRP SSN:</span>
                  <SSNCell referralId={id} ssnField="prp" maskedSsn={referral.ssn_prp_masked} hasSsn={true} token={token} onAuditEvent={() => {}} />
                </div>
              )}
              {!referral.has_ssn_omhc && !referral.has_ssn_prp && (
                <p className="text-xs text-slate-500 italic">No SSN on file</p>
              )}
            </div>
            <div className="mt-4 pt-3 border-t border-slate-700">
              <Field label="Referral ID" value={id} />
              <div className="mt-2">
                <Field label="Email Sent" value={referral.email_sent ? "Yes" : "No"} />
              </div>
            </div>
          </Section>

          {/* OMHC Client Info */}
          {(category === "OMHC" || category === "Both") && (
            <Section title="OMHC — Client Demographics" icon={User}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field label="First Name" value={omhc.firstName} />
                <Field label="Last Name" value={omhc.lastName} />
                <Field label="Middle Name" value={omhc.middleName} />
                <Field label="Legal Name" value={omhc.legalName} />
                <Field label="Date of Birth" value={omhc.dob} />
                <Field label="Age" value={omhc.age} />
                <Field label="Sex Assigned" value={omhc.sexAssigned} />
                <Field label="Gender Identity" value={omhc.genderIdentity} />
                <Field label="Pronouns" value={omhc.pronouns} />
                <Field label="Race" value={omhc.race} />
                <Field label="Ethnicity" value={omhc.ethnicity} />
                <Field label="Marital Status" value={omhc.maritalStatus} />
                <Field label="Living Situation" value={omhc.livingSituation} />
                <Field label="Employment" value={omhc.employmentStatus} />
                <Field label="Language" value={omhc.preferredLanguage} />
              </dl>
            </Section>
          )}

          {/* OMHC Clinical */}
          {(category === "OMHC" || category === "Both") && (
            <Section title="OMHC — Clinical Information" icon={Heart}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field label="Primary Diagnosis" value={omhc.primaryDiagnosis} />
                <Field label="Primary Code" value={omhc.primaryCode} />
                <Field label="Secondary Diagnosis" value={omhc.secondaryDiagnosis} />
                <Field label="Secondary Code" value={omhc.secondaryCode} />
                <Field label="Presenting Problem" value={omhc.presentingProblem} />
                <Field label="Current Medications" value={omhc.currentMedications} />
              </dl>
            </Section>
          )}

          {/* OMHC Insurance & Contact */}
          {(category === "OMHC" || category === "Both") && (
            <Section title="OMHC — Insurance & Contact" icon={Briefcase}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field label="Insurance Type" value={omhc.insuranceType} />
                <Field label="Medicaid ID" value={omhc.medicaidId} />
                <Field label="Phone" value={omhc.clientPhone} />
                <Field label="Address" value={[omhc.addressLine1, omhc.addressLine2, omhc.city, omhc.homeState, omhc.zip].filter(Boolean).join(", ")} />
              </dl>
            </Section>
          )}

          {/* PRP Client Info */}
          {(category === "PRP" || category === "Both") && (
            <Section title="PRP — Client Demographics" icon={User}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field label="First Name" value={prp.firstName} />
                <Field label="Last Name" value={prp.lastName} />
                <Field label="Middle Name" value={prp.middleName} />
                <Field label="Legal Name" value={prp.legalName} />
                <Field label="Date of Birth" value={prp.dob} />
                <Field label="Age" value={prp.age} />
                <Field label="Sex Assigned" value={prp.sexAssigned} />
                <Field label="Gender Identity" value={prp.genderIdentity} />
                <Field label="Pronouns" value={prp.pronouns} />
                <Field label="Race" value={prp.race} />
                <Field label="Ethnicity" value={prp.ethnicity} />
                <Field label="Marital Status" value={prp.maritalStatus} />
                <Field label="Living Situation" value={prp.livingSituation} />
                <Field label="Employment" value={prp.employmentStatus} />
                <Field label="Language" value={prp.preferredLanguage} />
              </dl>
            </Section>
          )}

          {/* PRP Clinical */}
          {(category === "PRP" || category === "Both") && (
            <Section title="PRP — Clinical Information" icon={Heart}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field label="Current Diagnosis" value={prp.currentDiagnosis} />
                <Field label="Diagnosis Code" value={prp.currentDiagnosisCode} />
                <Field label="Current Symptoms" value={prp.currentSymptoms} />
                <Field label="Reason for Referral" value={prp.reasonForReferral} />
                <Field label="Rehabilitation Goals" value={prp.rehabilitationGoals} />
                <Field label="Current OMHC" value={prp.currentOMHC} />
              </dl>
            </Section>
          )}

          {/* PRP Insurance & Contact */}
          {(category === "PRP" || category === "Both") && (
            <Section title="PRP — Insurance & Contact" icon={Briefcase}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field label="Insurance Type" value={prp.insuranceType} />
                <Field label="Insurance ID" value={prp.insuranceIdNumber} />
                <Field label="Phone" value={prp.clientPhone} />
                <Field label="Address" value={[prp.addressLine1, prp.addressLine2, prp.city, prp.homeState, prp.zip].filter(Boolean).join(", ")} />
              </dl>
            </Section>
          )}

          {/* Signatures */}
          {Object.keys(sigs).length > 0 && (
            <Section title="Signatures" icon={FileText}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field label="Under Supervision" value={sigs.underSupervision} />
                <Field label="Internal Status" value={sigs.internalStatus} />
                <Field label="Typed Signature" value={sigs.typedSignature} />
                <Field label="Has Drawn Signature" value={sigs.drawnSignature ? "Yes" : undefined} />
              </dl>
            </Section>
          )}
        </div>

        {/* Case Notes */}
        <Section title={`Case Notes (${notes.length})`} icon={MessageSquare}>
          {notes.length > 0 && (
            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
              {notes.map((n, idx) => (
                <div key={idx} className="bg-slate-900 rounded-lg px-3 py-2 border border-slate-700" data-testid={`detail-note-${idx}`}>
                  <p className="text-xs text-slate-200 whitespace-pre-wrap">{n.text}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[10px] text-slate-500">{n.author}</span>
                    <span className="text-[10px] text-slate-600">
                      {n.created_at ? new Date(n.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
              placeholder="Add a case note..."
              className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              data-testid="detail-note-input"
            />
            <button
              onClick={handleAddNote}
              disabled={noteSending || !newNote.trim()}
              className="px-3 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors disabled:opacity-50"
              data-testid="detail-note-send"
            >
              {noteSending ? <Loader2 className="w-3.5 h-3.5 text-white animate-spin" /> : <Send className="w-3.5 h-3.5 text-white" />}
            </button>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default ReferralDetailPage;
